import React, { FC, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  LinearProgress,
  TextField,
  Typography,
} from '@material-ui/core';
import Card from 'src/components/Card';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import MomentUtils from '@date-io/moment';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import DeliveryOrder, { CheckabledField, OrderArticle, PostDeliveryOrderBody } from 'src/interfaces/api/DeliveryOrder';
import { LegacyDeal, QuoteArticleHardware, QuoteArticleVirtual } from 'src/interfaces/api/Deal';
import DatePicker from 'src/components/DatePicker';
import moment from 'moment';
import { plurify } from 'src/utils/StringUtils';
import SignatureField from 'src/scenes/components/SignatureField';
import ConfirmationDialog from '../../../../components/ConfirmationDialog';
import AskBeforeLeaving from '../../../../components/AskBeforeLeaving';
import TimePicker from '../../../../components/TimePicker';
import Section from './components/Section';
import PhotoPicker from '../../../components/PhotoPicker';
import ProductsSection from '../../../DeliveryOrder/scenes/View/components/ProductsSection';

interface Props {
  onSaveDeliveryOrder: (DeliveryOrder: PostDeliveryOrderBody) => Promise<DeliveryOrder>;
  virtualArticles: QuoteArticleVirtual[];
  hardwareArticles: QuoteArticleHardware[];
  onSuccess: () => void;
  deal: LegacyDeal;
  contact: string;
}

const DeliveryOrder: FC<Props> = ({
  onSuccess,
  onSaveDeliveryOrder,
  virtualArticles,
  hardwareArticles,
  contact,
  deal,
}) => {
  const [formError, setFormError] = useState<string>();
  const [lessArticlesCount, setLessArticlesCount] = useState(0);
  const [isSendConfirmationDialogOpened, setIsSendConfirmationDialogOpened] = useState(false);

  const formMethods = useForm<PostDeliveryOrderBody>({
    defaultValues: {
      dealId: deal.id,
      articles: [],
      attachments: [],
      deliveryDate: moment().format(),
      startTime: moment().format('HH:mm'),
      endTime: moment().add(1, 'minute').format('HH:mm'),
      partialDelivery: false,
      signature: undefined,
    },
  });

  useEffect(() => {
    formMethods.setValue('contact', contact, { shouldValidate: true });
  }, [contact]);

  useEffect(() => {
    if (deal) {
      formMethods.setValue('socialReason', deal.customer?.installationSite?.socialReason || '');
      formMethods.setValue('address', deal.customer?.installationSite?.adress || '');
      formMethods.setValue('phoneNumber', deal.customer?.installationSite?.telOffice || '');
      formMethods.setValue('city', deal.customer?.installationSite?.postalCode || '');
    }
  }, [deal]);

  useEffect(() => {
    const productsHardware: CheckabledField<OrderArticle>[] = hardwareArticles.map((item) => ({
      code: item.id ? `${item.id}` : undefined,
      name: item.productReference?.wording || '',
      description: item.commercialWording || '',
      productReferenceId: item.productReference?.id,
      originalQuantity: item.quantity || 0,
      quantity: item.quantity || 0,
      enabled: true,
    }));

    const productsVirtual: CheckabledField<OrderArticle>[] = virtualArticles.map((item) => ({
      code: item.id ? `${item.id}` : undefined,
      name: item.productReference?.wording || '',
      description: item.commercialWording || '',
      productReferenceId: item.productReference?.id,
      originalQuantity: item.quantity || 0,
      quantity: item.quantity || 0,
      enabled: true,
    }));

    formMethods.setValue('articles', productsHardware.concat(productsVirtual), { shouldValidate: true });
  }, [virtualArticles, hardwareArticles]);

  const watchArticles = formMethods.watch('articles');
  const watchStartTime = formMethods.watch('startTime');

  useEffect(() => {
    if (watchArticles) {
      let lessQuantity = 0;
      watchArticles.forEach((article) => {
        if (!article.enabled || article.quantity < article.originalQuantity) {
          lessQuantity += article.enabled ? article.originalQuantity - article.quantity : article.originalQuantity;
        }
      });
      setLessArticlesCount(lessQuantity);
    }
  }, [JSON.stringify(watchArticles)]);

  const onSubmit = async (deliveryOrder: PostDeliveryOrderBody) => {
    try {
      const { articles, ...order } = deliveryOrder;
      const validatedArticles = articles.filter((article) => article.enabled && article.quantity > 0);

      await onSaveDeliveryOrder({ ...order, articles: validatedArticles });

      setLessArticlesCount(0);
      setFormError(undefined);
      onSuccess();
    } catch (e) {
      setFormError(e.message);
    }
  };

  const handleSubmit = () => {
    setIsSendConfirmationDialogOpened(false);
    formMethods.handleSubmit((data) => onSubmit({ ...data, toSend: true }))();
  };

  return (
    <FormProvider {...formMethods}>
      <AskBeforeLeaving
        message="Le formulaire n'a pas été enregistré, êtes-vous sûr de vouloir partir ?"
        condition={formMethods.formState.isDirty && !formMethods.formState.isSubmitting}
      />

      <MuiPickersUtilsProvider utils={MomentUtils}>
        <Box marginY='50px' display='flex' alignItems='center' clone>
          <Container maxWidth='md'>
            <Card>
              <Box marginBottom='24px !important' clone>
                <Typography variant='h5'>Bon de livraison</Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Controller
                    name='deliveryDate'
                    control={formMethods.control}
                    rules={{ required: { value: true, message: 'Le champ est requis' } }}
                    render={({ field, fieldState: { error } }) => (
                      <DatePicker
                        {...field}
                        label='Date de livraison'
                        format='DD/MM/YYYY'
                        inputVariant='outlined'
                        helperText={error?.message}
                        error={undefined !== error}
                        required
                        fullWidth
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={6} sm={4}>
                  <Controller
                    name='startTime'
                    control={formMethods.control}
                    rules={{ required: { value: true, message: 'Le champ est requis' } }}
                    render={({ field: { onChange, value, ...field }, fieldState: { error } }) => (
                      <TimePicker
                        {...field}
                        ampm={false}
                        value={value ? moment(value, 'HH:mm') : null}
                        onChange={(value) => onChange(value?.format('HH:mm'))}
                        label="Heure d'arrivée"
                        inputVariant='outlined'
                        helperText={error?.message}
                        error={undefined !== error}
                        required
                        fullWidth
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={6} sm={4}>
                  <Controller
                    name='endTime'
                    control={formMethods.control}
                    rules={{
                      required: { value: true, message: 'Le champ est requis' },
                      validate: (value) =>
                        moment(value, 'HH:mm').isAfter(moment(watchStartTime, 'HH:mm'), 'minute') ||
                        "L'heure de départ doit être après l'heure d'arrivée",
                    }}
                    render={({ field: { onChange, value, ...field }, fieldState: { error } }) => (
                      <TimePicker
                        {...field}
                        ampm={false}
                        value={value ? moment(value, 'HH:mm') : null}
                        onChange={(value) => onChange(value?.format('HH:mm'))}
                        label='Heure de départ'
                        inputVariant='outlined'
                        helperText={error?.message}
                        error={undefined !== error}
                        required
                        fullWidth
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name='socialReason'
                    control={formMethods.control}
                    render={({ field: { value, ...others }, fieldState: { error } }) => (
                      <TextField
                        {...others}
                        value={value || ''}
                        label='Raison sociale'
                        variant='outlined'
                        fullWidth
                        disabled
                        error={undefined !== error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name='phoneNumber'
                    control={formMethods.control}
                    render={({ field: { value, ...others }, fieldState: { error } }) => (
                      <TextField
                        {...others}
                        value={value || ''}
                        label='Téléphone'
                        variant='outlined'
                        fullWidth
                        disabled
                        error={undefined !== error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={8}>
                  <Controller
                    name='address'
                    control={formMethods.control}
                    render={({ field: { value, ...others }, fieldState: { error } }) => (
                      <TextField
                        {...others}
                        value={value || ''}
                        label='Adresse'
                        variant='outlined'
                        fullWidth
                        disabled
                        error={undefined !== error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Controller
                    name='city'
                    control={formMethods.control}
                    render={({ field: { value, ...others }, fieldState: { error } }) => (
                      <TextField
                        {...others}
                        value={value || ''}
                        label='Ville'
                        variant='outlined'
                        fullWidth
                        disabled
                        error={undefined !== error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Section variant='overline'>Details</Section>
                </Grid>

                <Grid item xs={12}>
                  <ProductsSection virtualArticles={virtualArticles} hardwareArticles={hardwareArticles} />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name='attachments'
                    control={formMethods.control}
                    render={({ field: { ref, ...otherField }, fieldState: { error } }) => (
                      <PhotoPicker {...otherField} error={undefined !== error} helperText={error?.message} />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Section variant='overline'>Informations complementaires</Section>

                  {lessArticlesCount > 0 && (
                    <Grid item xs={12}>
                      <Section variant='caption'>
                        Il y a {lessArticlesCount} {plurify('article', lessArticlesCount)} manquant dans cette commande.
                      </Section>
                    </Grid>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name='partialDelivery'
                    control={formMethods.control}
                    render={({ field: { value, ...field } }) => (
                      <FormControlLabel
                        control={<Checkbox size='small' />}
                        label='Livraison partielle'
                        checked={value}
                        {...field}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name='observations'
                    control={formMethods.control}
                    render={({ field: { value, ...field }, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label='Observations'
                        variant='outlined'
                        multiline
                        value={value || ''}
                        minRows={3}
                        fullWidth
                        error={undefined !== error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name='contact'
                    control={formMethods.control}
                    rules={{ required: { value: true, message: 'Le champ est requis' } }}
                    render={({ field: { value, ...others }, fieldState: { error } }) => (
                      <TextField
                        {...others}
                        value={value || ''}
                        label='Contact'
                        variant='outlined'
                        fullWidth
                        required
                        error={undefined !== error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <SignatureField />
                </Grid>

                <Box display='flex' flexDirection='column' clone>
                  <Grid item xs={12}>
                    <Section variant='overline'>Envoyez le bon de livraison aux adresses suivantes</Section>
                    <Section variant='caption'>Séparez les mails par des virgules</Section>
                  </Grid>
                </Box>

                <Grid item xs={12}>
                  <Controller
                    name='recipientEmails'
                    control={formMethods.control}
                    render={({ field: { value, ...others }, fieldState: { error } }) => (
                      <TextField
                        {...others}
                        value={value || ''}
                        label='Adresses mails'
                        variant='outlined'
                        fullWidth
                        error={undefined !== error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Grid>

                {formError && (
                  <Grid item xs={12}>
                    <Box component='p' color='#f44336' fontSize='0.75rem' paddingX='25px'>
                      {formError}
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Grid container justifyContent='flex-end'>
                    <Grid item xs={4}>
                      <Button
                        fullWidth
                        color='primary'
                        variant='contained'
                        disabled={formMethods.formState.isSubmitting}
                        onClick={() => setIsSendConfirmationDialogOpened(true)}
                      >
                        Envoyer
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              {formMethods.formState.isSubmitting && <LinearProgress style={{ marginTop: 5 }} />}
            </Card>
          </Container>
        </Box>

        <ConfirmationDialog
          open={isSendConfirmationDialogOpened}
          title='Attention'
          description={`Vous êtes sur le point d'envoyer le bon de livraison.\nIl sera envoyé aux adresses que vous avez défini.\nUne fois envoyé, il vous sera impossible de l'editer.`}
          confirmationText='Valider'
          onValidate={handleSubmit}
          onClose={() => setIsSendConfirmationDialogOpened(false)}
        />
      </MuiPickersUtilsProvider>
    </FormProvider>
  );
};

export default DeliveryOrder;
